import { useQuery } from '@powersync/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  addBusinessDays,
  DomainError,
  highlightLimitReached,
  normalizeTagName,
  triageItem,
  type LocalOperation,
  type TriageDecision,
  type TriageItemInput,
} from '@flow/domain';
import {
  applyLocalOperations,
  queries,
  type ActiveHighlightRow,
  type CaptureDestinationRow,
  type CaptureTagRow,
  type CaptureTaxonomyRow,
  type FieldValueRow,
  type HighlightTypeRow,
  type InboxItemRow,
  type WorkspaceSummary,
} from '@flow/sync';
import {
  FollowUpDialog,
  HighlightLimitDialog,
  TriageScreen,
  type TriageDestination,
} from '@flow/ui/components';

import { buildTagIndex, formatDay, interpret, type TagIndex } from '../capture/model.js';
import { useToday } from '../capture/use-today.js';
import { SyncStatus } from '../components/SyncStatus.jsx';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { inboxTags, tagLabel } from '../inbox/InboxView.jsx';
import { useSession } from '../session.jsx';
import { isTypingTarget } from '../shell/use-shortcuts.js';

/**
 * «Procesar Inbox» (TRI-01..03, TRI-06, docs/04 §2): the queue is the watched Inbox query plus the
 * decisions of this session. Each decision is one `triageItem` transaction; its undo operations
 * are kept in memory for `Z` (design D2). Keys come from the template's `triage.keys` (design D3).
 */

/** Destinations of F1 (docs/03 §4.6): P and R arrive in F2. */
const KEYS = {
  A: { stageKey: 'asap' },
  B: { stageKey: 'backlog' },
  G: { stageKey: 'someday' },
  S: { stageKey: 'waiting' },
  H: {},
  X: {},
} as const;
type DestinationKey = keyof typeof KEYS;
const DESTINATION_KEYS = Object.keys(KEYS) as DestinationKey[];

interface Decided {
  id: string;
  title: string;
  destination: string;
  undo: LocalOperation[];
  /** Enter on suggestions: undoable, but not a processed element. */
  editOnly: boolean;
}

interface Draft {
  itemId: string;
  text: string;
  dismissed: string[];
}

type Pending =
  | { kind: 'follow-up'; item: InboxItemRow }
  | {
      kind: 'limit';
      item: InboxItemRow;
      current: ActiveHighlightRow[];
      type: HighlightTypeRow;
      periodId: string;
    };

export function TriagePage() {
  const { user, local } = useSession();
  if (!user || !local) return null;
  return <Triage userId={user.id} local={local} />;
}

function useRows<T>(query: { sql: string; params: (string | number | null)[] }) {
  return useQuery<T>(query.sql, query.params);
}

function Triage({ userId, local }: { userId: string; local: LocalDatabase }) {
  const navigate = useNavigate();
  const today = useToday(userId);
  const { data: workspaces } = useRows<WorkspaceSummary>(queries.personalWorkspace());
  const workspaceId = workspaces[0]?.id ?? '';
  const { data: stages } = useRows<CaptureDestinationRow>(queries.captureDestinations(workspaceId));
  const inbox = stages.find((stage) => stage.is_capture_default === 1);
  const { data: items, isLoading } = useRows<InboxItemRow>(queries.inboxItems(inbox?.id ?? ''));
  const { data: types } = useRows<HighlightTypeRow>(queries.highlightTypeByKey(userId, 'tai'));
  const { data: waitingField } = useRows<{ id: string }>(
    queries.fieldDefByKey(workspaceId, 'waiting_on'),
  );
  const { data: followUpField } = useRows<{ id: string }>(
    queries.fieldDefByKey(workspaceId, 'follow_up_on'),
  );

  const [decided, setDecided] = useState<Decided[]>([]);
  const [cursor, setCursor] = useState(0);
  const [pinned, setPinned] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [index, setIndex] = useState<TagIndex>({ taxonomies: new Map(), tags: new Map() });
  const [persons, setPersons] = useState<{ id: string; name: string }[]>([]);
  const busy = useRef(false);

  // Tags of the workspace, for suggestions and for the persons of the follow-up.
  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    void readTags(local.db, workspaceId).then((next) => {
      if (cancelled) return;
      setIndex(next.index);
      setPersons(next.persons);
    });
    return () => {
      cancelled = true;
    };
  }, [local.db, workspaceId, decided.length]);

  // The pinned element (after an undo) is the current one as soon as it is back in the Inbox.
  const pinnedIndex = pinned ? items.findIndex((item) => item.id === pinned) : -1;
  const position = pinnedIndex >= 0 ? pinnedIndex : Math.min(cursor, Math.max(0, items.length - 1));
  const current = items[position] ?? null;
  const stageByKey = (key: string) => stages.find((stage) => stage.key === key);

  // Suggestions of the edited title (TRI-03), for the current element only.
  const activeDraft = current && draft?.itemId === current.id ? draft : null;
  const interpretation = useMemo(
    () =>
      activeDraft
        ? interpret(activeDraft.text, { today, dismissed: activeDraft.dismissed, index })
        : null,
    [activeDraft, today, index],
  );
  const assignedTags = current ? inboxTags(current) : [];
  const suggestions = interpretation
    ? interpretation.chips.map((chip) => ({ key: chip.key, label: `${chip.label}: ${chip.value}` }))
    : [];

  const resetItemState = () => {
    setArmed(false);
    setEditing(false);
    setDraft(null);
  };

  /** Accepted suggestions of the current draft, as `triageItem` edits. */
  const edits = (): TriageItemInput['edits'] => {
    if (!interpretation || !current) return undefined;
    const { parse, tags } = interpretation;
    const title = parse.title.trim();
    return {
      ...(title && title !== current.title ? { title } : {}),
      ...(parse.due_on ? { due_on: parse.due_on } : {}),
      ...(parse.start_on ? { start_on: parse.start_on } : {}),
      tags: tags.map((tag) => tag.input),
      assigned_tag_ids: assignedTags.map((tag) => tag.id),
    };
  };

  const snapshot = (item: InboxItemRow): TriageItemInput['item'] => ({
    id: item.id,
    workspace_id: item.workspace_id,
    kind: item.kind as 'item',
    stage_id: item.stage_id,
    stage_changed_at: item.stage_changed_at,
    status: item.status as 'open',
    completed_at: item.completed_at,
    deleted_at: item.deleted_at,
    title: item.title,
    due_on: item.due_on,
    start_on: item.start_on,
  });

  const run = async (
    item: InboxItemRow,
    decision: TriageDecision,
    label: string,
    highlight?: TriageItemInput['highlight'],
  ) => {
    if (busy.current) return;
    busy.current = true;
    try {
      // X takes the element's live descendants with it (INV-12, f1-trash-search D1).
      const descendants =
        decision.type === 'trash'
          ? (
              await local.db.getAll<{ id: string }>(
                queries.liveDescendants(item.id).sql,
                queries.liveDescendants(item.id).params,
              )
            ).map((row) => row.id)
          : [];
      const { operations, undo } = triageItem({
        user_id: userId,
        item: snapshot(item),
        decision,
        descendants,
        edits: edits(),
        ...(highlight ? { highlight } : {}),
      });
      await applyLocalOperations(local.db, operations);
      const editOnly = decision.type === 'edit';
      setDecided((list) => [
        ...list,
        { id: item.id, title: item.title, destination: label, undo, editOnly },
      ]);
      if (editOnly) {
        setEditing(false);
        setDraft(null);
        setAnnouncement(t.triage.accepted);
      } else {
        setPinned(null);
        resetItemState();
        const next = items.filter((row) => row.id !== item.id)[position];
        setAnnouncement(t.triage.sent(label, next?.title ?? null));
      }
    } catch (cause) {
      console.error('[flow] decisión de triaje no aplicada', cause);
      setNotice(cause instanceof DomainError ? cause.message : t.triage.error);
    } finally {
      busy.current = false;
    }
  };

  /** A (optionally with the TAI armed), B, G: the stage of the template key. */
  const toStage = async (key: 'A' | 'B' | 'G', item: InboxItemRow) => {
    const stage = stageByKey(KEYS[key].stageKey);
    if (!stage) return;
    const decision: TriageDecision = { type: 'stage', stage: stageInput(stage) };
    const label = t.triage.destinations[key].label;
    const type = types[0];
    if (!armed || !type) {
      if (armed) setNotice(t.triage.taiOnlyAsap);
      await run(item, decision, label);
      return;
    }
    if (key !== 'A') {
      // TAI-02: the destination is not eligible; the decision goes on without it.
      setNotice(t.triage.taiOnlyAsap);
      await run(item, decision, label);
      return;
    }
    const periodQuery = queries.openPeriod(userId);
    const period = await local.db.getOptional<{ id: string }>(periodQuery.sql, periodQuery.params);
    if (!period) {
      setNotice(t.triage.noPeriod);
      return;
    }
    const activeQuery = queries.activeHighlights(userId, period.id, type.id);
    const active = await local.db.getAll<ActiveHighlightRow>(activeQuery.sql, activeQuery.params);
    if (highlightLimitReached(type, active.length)) {
      setPending({ kind: 'limit', item, current: active, type, periodId: period.id });
      return;
    }
    await run(item, decision, `${label} · ${type.key.toUpperCase()}`, {
      type: highlightType(type),
      period_id: period.id,
      current: active,
      mode: 'add',
    });
  };

  const decide = async (key: DestinationKey) => {
    if (!current || pending) return;
    setNotice(null);
    if (key === 'A' || key === 'B' || key === 'G') {
      await toStage(key, current);
    } else if (key === 'S') {
      if (armed) setNotice(t.triage.taiOnlyAsap);
      setPending({ kind: 'follow-up', item: current });
    } else if (key === 'H') {
      if (armed) setNotice(t.triage.taiOnlyAsap);
      await run(current, { type: 'done' }, t.triage.destinations.H.label);
    } else {
      await run(current, { type: 'trash' }, t.triage.destinations.X.label);
    }
  };

  const undoLast = async () => {
    const last = decided.at(-1);
    if (!last || busy.current) return;
    busy.current = true;
    try {
      await applyLocalOperations(local.db, last.undo);
      setDecided((list) => list.slice(0, -1));
      resetItemState();
      setPinned(last.id);
      setAnnouncement(t.triage.undone(last.title));
    } finally {
      busy.current = false;
    }
  };

  const move = (step: 1 | -1) => {
    if (items.length === 0) return;
    resetItemState();
    setPinned(null);
    setCursor(Math.min(items.length - 1, Math.max(0, position + step)));
  };

  const exit = () => void navigate({ to: '/v/$viewKey', params: { viewKey: 'inbox' } });

  const startEditing = () => {
    if (!current) return;
    setEditing(true);
    setDraft((previous) =>
      previous?.itemId === current.id
        ? previous
        : { itemId: current.id, text: current.title, dismissed: [] },
    );
  };

  // Keys of the triage (docs/05 §6.1). Off while typing and while a dialog is open.
  const toggleArmed = () => setArmed((value) => !value);
  const handlers = useRef({ decide, undoLast, move, exit, startEditing, toggleArmed, pending });
  useEffect(() => {
    handlers.current = { decide, undoLast, move, exit, startEditing, toggleArmed, pending };
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const h = handlers.current;
      if (h.pending || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      const key = event.key.toUpperCase();
      if (event.key === 'Escape') {
        event.preventDefault();
        h.exit();
      } else if ((DESTINATION_KEYS as string[]).includes(key)) {
        event.preventDefault();
        void h.decide(key as DestinationKey);
      } else if (key === 'I') {
        event.preventDefault();
        h.toggleArmed();
      } else if (key === 'Z') {
        event.preventDefault();
        void h.undoLast();
      } else if (key === 'J' || key === 'K') {
        event.preventDefault();
        h.move(key === 'J' ? 1 : -1);
      } else if (key === 'E') {
        event.preventDefault();
        h.startEditing();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!inbox || (isLoading && items.length === 0)) {
    return <p className="muted">{t.app.loading}</p>;
  }

  const processed = decided.filter((entry) => !entry.editOnly);
  const typeLabel = types[0]?.key.toUpperCase() ?? 'TAI';
  const destinations: TriageDestination[] = DESTINATION_KEYS.map((key) => ({
    key,
    label: t.triage.destinations[key].label,
    hint: t.triage.destinations[key].hint,
    ...(key === 'A' && armed ? { badge: `+ ${typeLabel}` } : {}),
  }));
  const remaining = items.length;
  const totalLabel =
    remaining >= 200 ? `${processed.length + remaining}+` : String(processed.length + remaining);

  return (
    <>
      <TriageScreen
        position={current ? processed.length + position + 1 : processed.length}
        total={totalLabel}
        progress={processed.length / Math.max(1, processed.length + remaining)}
        processed={processed.map((entry) => ({
          id: entry.id,
          title: entry.title,
          destination: entry.destination,
        }))}
        current={current ? { id: current.id, title: current.title } : null}
        next={items
          .slice(position + 1, position + 6)
          .map((row) => ({ id: row.id, title: row.title }))}
        more={Math.max(0, items.length - position - 6)}
        assigned={[
          ...(current?.due_on
            ? [{ key: 'due', label: `${t.quickCapture.due}: ${formatDay(current.due_on, today)}` }]
            : []),
          ...(current?.start_on
            ? [
                {
                  key: 'start',
                  label: `${t.quickCapture.start}: ${formatDay(current.start_on, today)}`,
                },
              ]
            : []),
          ...assignedTags.map((tag) => ({ key: tag.id, label: tagLabel(tag) })),
        ]}
        suggestions={suggestions}
        onDismissSuggestion={(key) =>
          setDraft((value) => (value ? { ...value, dismissed: [...value.dismissed, key] } : value))
        }
        editing={
          editing && activeDraft
            ? {
                value: activeDraft.text,
                marks: interpretation?.chips ?? [],
                onChange: (text) => setDraft({ ...activeDraft, text }),
                onKeyDown: (event) => {
                  if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    if (current) void run(current, { type: 'edit' }, t.triage.edited);
                  } else if (event.key === 'Escape') {
                    // Leave the field; the suggestions stay pending for a destination key.
                    event.preventDefault();
                    event.stopPropagation();
                    setEditing(false);
                  }
                },
              }
            : null
        }
        onEdit={startEditing}
        destinations={destinations}
        onDestination={(key) => void decide(key as DestinationKey)}
        notice={notice}
        announcement={announcement}
        onExit={exit}
        status={<SyncStatus local={local} />}
        finished={
          <div className="triage__finished">
            <h2>{t.triage.empty}</h2>
            <button type="button" onClick={exit}>
              {t.triage.backToInbox}
            </button>
          </div>
        }
        labels={{
          exit: t.triage.exit,
          title: t.triage.title,
          subtitle: t.triage.subtitle,
          of: t.triage.of,
          progress: t.triage.progress,
          queue: t.triage.queue,
          processedHeading: t.triage.processed,
          currentHeading: t.triage.current,
          nextHeading: t.triage.next,
          more: t.triage.more,
          question: t.triage.question,
          edit: t.triage.edit,
          editField: t.triage.editField,
          assigned: t.triage.assigned,
          detected: t.triage.detected,
          acceptSuggestions: t.triage.acceptSuggestions,
          removeSuggestion: t.triage.removeSuggestion,
          whereTo: t.triage.whereTo,
          whereToHint: t.triage.whereToHint,
          destination: (destination) =>
            t.triage.destinationName(destination.label, destination.hint, destination.badge),
          footer: t.triage.footer(typeLabel),
        }}
      />
      {pending?.kind === 'follow-up' ? (
        <FollowUpDialog
          persons={persons}
          defaultDate={addBusinessDays(today, 3)}
          normalize={normalizeTagName}
          onCancel={() => setPending(null)}
          onConfirm={({ person, date }) => {
            const stage = stageByKey(KEYS.S.stageKey);
            const taxonomy = index.taxonomies.get('+');
            const waitingOn = waitingField[0];
            const followUpOn = followUpField[0];
            const item = pending.item;
            setPending(null);
            if (!stage || !taxonomy || !waitingOn || !followUpOn) {
              setNotice(t.triage.error);
              return;
            }
            void (async () => {
              const previousQuery = queries.blockFieldValues(item.id);
              const previous = await local.db.getAll<FieldValueRow>(
                previousQuery.sql,
                previousQuery.params,
              );
              await run(
                item,
                {
                  type: 'waiting',
                  stage: stageInput(stage),
                  waiting_on: {
                    taxonomy: {
                      id: taxonomy.id,
                      workspace_id: taxonomy.workspace_id,
                      cardinality: taxonomy.cardinality,
                    },
                    path: [
                      person.id
                        ? { name: person.name, existing_id: person.id }
                        : { name: person.name },
                    ],
                  },
                  follow_up_on: date,
                  fields: { waiting_on: waitingOn.id, follow_up_on: followUpOn.id },
                  previous: previous
                    .filter(
                      (row) => row.field_id === waitingOn.id || row.field_id === followUpOn.id,
                    )
                    .map(fieldValueSnapshot),
                },
                t.triage.destinations.S.label,
              );
            })();
          }}
          labels={t.triage.followUp}
        />
      ) : null}
      {pending?.kind === 'limit' ? (
        <HighlightLimitDialog
          mode={pending.type.limit_mode}
          message={t.triage.limit.message(pending.current.length, pending.type.max_per_period ?? 0)}
          current={pending.current.map((row) => row.title ?? '')}
          onCancel={() => setPending(null)}
          onReplace={() => {
            const { item, current: active, type, periodId } = pending;
            setPending(null);
            void toStageWith(item, type, periodId, active, 'replace');
          }}
          onAddAnyway={() => {
            const { item, current: active, type, periodId } = pending;
            setPending(null);
            void toStageWith(item, type, periodId, active, 'add', true);
          }}
          labels={t.triage.limit}
        />
      ) : null}
    </>
  );

  async function toStageWith(
    item: InboxItemRow,
    type: HighlightTypeRow,
    periodId: string,
    active: ActiveHighlightRow[],
    mode: 'add' | 'replace',
    force = false,
  ) {
    const stage = stageByKey(KEYS.A.stageKey);
    if (!stage) return;
    await run(
      item,
      { type: 'stage', stage: stageInput(stage) },
      `${t.triage.destinations.A.label} · ${type.key.toUpperCase()}`,
      { type: highlightType(type), period_id: periodId, current: active, mode, force },
    );
  }
}

function stageInput(stage: CaptureDestinationRow) {
  return {
    id: stage.id,
    workspace_id: stage.workspace_id,
    category: stage.category,
    kind_scope: stage.kind_scope,
  };
}

function highlightType(type: HighlightTypeRow) {
  return {
    id: type.id,
    max_per_period: type.max_per_period,
    limit_mode: type.limit_mode,
    eligible_kind: type.eligible_kind,
    eligible_stage_category: type.eligible_stage_category,
  };
}

/** Tags of the prefixed taxonomies: the index for suggestions, and the persons (`+`) by name. */
async function readTags(db: LocalDatabase['db'], workspaceId: string) {
  const taxonomiesQuery = queries.captureTaxonomies(workspaceId);
  const tagsQuery = queries.captureTags(workspaceId);
  const [taxonomies, tags] = await Promise.all([
    db.getAll<CaptureTaxonomyRow>(taxonomiesQuery.sql, taxonomiesQuery.params),
    db.getAll<CaptureTagRow>(tagsQuery.sql, tagsQuery.params),
  ]);
  const personTaxonomy = taxonomies.find((taxonomy) => taxonomy.prefix === '+');
  const persons = tags
    .filter(
      (tag) =>
        tag.taxonomy_id === personTaxonomy?.id && tag.parent_id === null && !tag.merged_into_id,
    )
    .map((tag) => ({ id: tag.id, name: tag.name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  return { index: buildTagIndex(taxonomies, tags), persons };
}

/** A stored field value as the snapshot the undo restores (SQLite keeps JSON as text). */
function fieldValueSnapshot(row: FieldValueRow) {
  return {
    ...row,
    value_bool: row.value_bool === null ? null : row.value_bool === 1 ? (1 as const) : (0 as const),
    value_json: row.value_json === null ? null : (JSON.parse(row.value_json) as string),
  };
}
